<script setup>
import { ref, watch } from 'vue'
import { useConfirm } from 'primevue/useconfirm'
import { trans } from 'laravel-vue-i18n'
import draggable from 'vuedraggable'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'

const props = defineProps({
    sections: { type: Array, default: () => [] },
    activeId: { type: [Number, String], default: null },
})

const SECTION_DOT_COLORS = ['#003CFF', '#1F8A5B', '#D9663A']

function sectionColor(section) {
    const index = props.sections.findIndex((s) => s.id === section.id)
    return SECTION_DOT_COLORS[index % SECTION_DOT_COLORS.length]
}

const emit = defineEmits(['select', 'create', 'rename', 'delete', 'reorder'])

const model = ref(props.sections)

watch(() => props.sections, (sections) => {
    model.value = sections
})

const renamingId = ref(null)
const renameValue = ref('')

function startRename(section) {
    renamingId.value = section.id
    renameValue.value = section.title
}

function commitRename(section) {
    if (renameValue.value.trim() && renameValue.value !== section.title) {
        emit('rename', { id: section.id, title: renameValue.value.trim() })
    }
    renamingId.value = null
}

const confirm = useConfirm()

function askDelete(event, section) {
    confirm.require({
        target: event.currentTarget,
        message: trans('contacts.notebook_confirm_delete'),
        icon: 'pi pi-exclamation-triangle',
        rejectProps: { label: trans('common.cancel'), severity: 'secondary', outlined: true },
        acceptProps: { label: trans('common.delete'), severity: 'danger' },
        accept: () => emit('delete', section.id),
    })
}

function onDragEnd() {
    emit('reorder', model.value.map((s, index) => ({ id: s.id, sort_order: (index + 1) * 10 })))
}
</script>

<template>
    <div class="w-56 h-full flex-shrink-0 flex flex-col p-3 md:p-4  gap-1.5 border-r dark:border-gray-700 pr-2.5">


        <draggable
            v-model="model"
            item-key="id"
            handle=".drag-handle"
            animation="150"
            class="flex flex-col gap-0.5 min-h-0 overflow-y-auto"
            @end="onDragEnd"
        >
            <template #item="{ element: section }">
                <div
                    class="group flex items-center gap-2 px-2 py-1.5 cursor-pointer text-sm border-l-[3px]"
                    :class="activeId === section.id
                        ? 'bg-neutral-50 dark:bg-gray-800 text-neutral-800 dark:text-neutral-100 font-medium border-roori-blue'
                        : 'hover:text-neutral-600 dark:hover:text-neutral-300 text-neutral-500 dark:text-neutral-400 border-transparent'"
                    @click="renamingId !== section.id && emit('select', section.id)"
                >
                    <i class="drag-handle pi pi-bars !text-[11px] text-neutral-400 cursor-grab flex-shrink-0"></i>
                    <i class="w-[7px] h-[7px] rounded-full flex-shrink-0" :style="{ background: sectionColor(section) }"></i>

                    <InputText
                        v-if="renamingId === section.id"
                        v-model="renameValue"
                        size="small"
                        class="!py-0.5 !text-sm flex-1"
                        autofocus
                        @keyup.enter="commitRename(section)"
                        @blur="commitRename(section)"
                        @click.stop
                    />
                    <span v-else class="flex-1 truncate" @dblclick.stop="startRename(section)">
                        {{ section.title }}
                    </span>

                    <button
                        type="button"
                        class="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-500 flex-shrink-0"
                        @click.stop="askDelete($event, section)"
                    >
                        <i class="pi pi-trash !text-[11px]"></i>
                    </button>
                </div>
            </template>
        </draggable>

        <Button
            :label="$t('contacts.notebook_new_section')"
            icon="pi pi-plus"
            text
            size="small"
            class="!justify-start"
            @click="emit('create')"
        />

        <p v-if="!sections.length" class="text-xs text-neutral-400 px-2">
            {{ $t('contacts.notebook_empty_sidebar') }}
        </p>
    </div>
</template>
